// Sentient Vault — Complete Application Internationalization
// Architecture: all visible text from translation keys, domain labels from codes

const en = {
  // Common
  "common.total":"Total","common.amount":"Amount","common.date":"Date","common.status":"Status",
  "common.priority":"Priority","common.actions":"Actions","common.save":"Save","common.cancel":"Cancel",
  "common.close":"Close","common.loading":"Loading...","common.noData":"No data available",
  "common.confirm":"Confirm","common.back":"Back","common.next":"Next","common.submit":"Submit",
  "common.refresh":"Refresh","common.search":"Search","common.export":"Export",
  // Trust
  "trust.banner":"Sentient Vault does not hold or control your funds. It analyses your financial data to show what is truly yours.",
  "trust.readonly":"Read-only connection. We never hold your funds.",
  "trust.noncustodial":"Non-custodial — we never hold your funds.",
  "trust.noBlock":"This system does not move or restrict your money. It shows the financial consequences of your decisions.",
  // Sidebar
  "sidebar.dashboard":"Dashboard","sidebar.bank":"Bank & Transactions","sidebar.import":"Data Import",
  "sidebar.bubbles":"Value Bubbles","sidebar.vaults":"Vaults","sidebar.obligations":"Obligations",
  "sidebar.counterparties":"Counterparties",
  "sidebar.report":"Truth Report","sidebar.insights":"Insights","sidebar.sentinel":"Sentinel AI",
  "sidebar.dataPool":"Data Pool",
  "sidebar.settings":"Settings",
  "sidebar.version":"Global",
  // Ownership
  "own.title":"Ownership Truth","own.owned":"Owned Capital","own.custody":"Custody Funds",
  "own.restricted":"Restricted Capital","own.assigned":"Assigned Funds",
  // Domain: Vault types
  "vault.title":"Vault Architecture","vault.viewAll":"View All",
  "vault.TAX_CUSTODY":"Tax Custody","vault.SUPPLIER_OBLIGATION":"Supplier Obligation",
  "vault.CUSTOMER_TRUST":"Customer Trust","vault.INVENTORY_CUSTODY":"Inventory Custody",
  "vault.OPERATING_CUSTODY":"Operating Custody","vault.FREE_EQUITY":"Free Equity",
  "vault.CREDIT_FACILITY_CUSTODY":"Credit Facility","vault.LOAN_OBLIGATION":"Loan Obligation",
  "vault.RESERVE_CAPITAL":"Reserve Capital","vault.STRATEGIC_CAPITAL":"Strategic Capital",
  "vault.permission":"Permission","vault.balance":"Current Balance","vault.history":"30-Day Balance History",
  "vault.guardrailSim":"Guardrail Simulator","vault.fromVault":"From Vault",
  "vault.previewSpend":"Preview Spend","vault.requestOverride":"Request Override",
  // Domain: Bubble components
  "comp.VAT":"VAT","comp.NET_REVENUE":"Net Revenue","comp.SUPPLIER_CUSTODY":"Supplier Hold",
  "comp.OVERHEAD":"Overhead","comp.PROFIT_CANDIDATE":"True Profit","comp.CUSTOMER_DEPOSIT":"Customer Deposit",
  "comp.LOAN_PROCEEDS":"Loan Proceeds","comp.PAYMENT":"Payment",
  // Bubbles
  "bubble.title":"Value Bubble Ledger","bubble.subtitle":"Per-transaction economic truth — every payment decomposed into ownership states",
  "bubble.latest":"Latest Value Bubble","bubble.gross":"Gross","bubble.net":"Net","bubble.tax":"Tax",
  "bubble.costs":"Direct Costs","bubble.overhead":"Overhead","bubble.trueProfit":"True Profit",
  "bubble.decomposition":"Ownership Decomposition","bubble.vaultRouting":"Vault Routing",
  // Obligations
  "obl.title":"Obligations Planner","obl.subtitle":"Coverage ratios, payment scheduling, and risk projections",
  "obl.totalDue":"Total Due","obl.count":"Obligations","obl.critical":"Critical","obl.nextDue":"Next Due",
  "obl.calendar":"Obligation Calendar","obl.upcoming":"Upcoming Obligations",
  "obl.generateSchedule":"Generate Schedule","obl.paymentSchedule":"Proposed Payment Schedule",
  "obl.claimant":"Claimant","obl.type":"Type","obl.dueDate":"Due","obl.coverage":"Coverage",
  // Behaviour
  "beh.title":"Behavioural Intelligence","beh.subtitle":"Financial conduct analysis, coaching, and triune-state governance",
  "beh.currentState":"Current Behavioural State","beh.metrics":"Metrics","beh.timeline":"State Timeline",
  "beh.coaching":"Coaching Feed","beh.overrides":"Override Log",
  "beh.CUSTODY_DISCIPLINE_INDEX":"Custody Discipline","beh.OVERRIDE_RELIANCE_INDEX":"Override Risk",
  "beh.FINANCIAL_STRESS_REACTIVITY":"Stress Reactivity","beh.PLANNING_CONSISTENCY_RATIO":"Planning Consistency",
  "beh.STRATEGIC_MATURITY_INDEX":"Strategic Maturity",
  "beh.STRATEGIC":"Strategic","beh.EMOTIONAL":"Emotional","beh.SURVIVAL":"Survival",
  // Sentinel
  "ai.title":"The Sentinel","ai.subtitle":"Your AI financial guardian — observing, advising, never interfering",
  "ai.observing":"Observing","ai.watching":"The Sentinel is watching",
  "ai.askPrompt":"Ask the Sentinel about your finances...","ai.analysing":"Sentinel is analysing...",
  "ai.liveInsights":"Live Insights","ai.quickQuestions":"Quick Questions",
  // Bank
  "bank.title":"Bank Connections","bank.subtitle":"Read-only mirroring. Sentient Vault never holds your funds.",
  "bank.connect":"Connect Bank","bank.autoMatch":"Auto-Match","bank.sync":"Sync",
  "bank.totalTxn":"Total Transactions","bank.matched":"Matched","bank.unmatched":"Unmatched",
  "bank.matchRate":"Match Rate","bank.fix":"Fix","bank.reconcile":"Reconcile",
  // Ingest
  "ingest.title":"Data Ingestion","ingest.subtitle":"Upload financial data or create entries manually",
  "ingest.csvUpload":"CSV Upload","ingest.manualEntry":"Manual Entry",
  "ingest.dropCSV":"Drop CSV file here or click to browse","ingest.mapping":"Column Mapping",
  "ingest.newInvoice":"New Invoice","ingest.newBill":"New Bill",
  // Report
  "report.title":"Financial Truth Report","report.download":"Download PDF","report.share":"Share",
  "report.ownership":"Ownership Summary","report.vaults":"Vault Breakdown",
  "report.obligations":"Obligation Summary","report.profit":"True Profit Analysis",
  "report.behaviour":"Behaviour Score","report.risks":"Risk Summary","report.ai":"AI Commentary",
  "report.totalTracked":"Total Tracked Value","report.noRisks":"No significant risks detected",
  "report.grossRevenue":"Gross Revenue","report.totalExpenses":"Total Expenses",
  "report.adjustments":"Adjustments","report.profitCandidate":"Profit Candidate",
  "report.trueAvailable":"True Available Profit",
  // Recovery
  "recovery.healthStable":"Financial Health: Stable","recovery.risksDetected":"Risks Detected",
  "recovery.actions":"Recovery Actions","recovery.simulate":"Simulate",
  "recovery.coaching":"Behaviour Coaching","recovery.showPlan":"Show Recovery Plan",
  // What-If
  "whatif.title":"\"What If?\" Simulator","whatif.simulate":"Simulate",
  "whatif.coverage":"Coverage","whatif.runway":"Runway","whatif.balanceAfter":"Balance After",
  "whatif.alternatives":"Alternatives","whatif.crunchDate":"Crunch Date",
  // Timeline
  "timeline.title":"Financial Timeline",
  // Narration
  "narration.title":"Your Financial Reality","narration.aiGenerated":"AI-generated insight",
  // Explain
  "explain.whatIs":"What this is","explain.why":"Why it is here","explain.affects":"What it affects",
  "explain.next":"What happens next","explain.risk":"Risk / Warning","explain.breakdown":"Breakdown",
  // Fix
  "fix.title":"Fix Transaction","fix.subtitle":"Classify this transaction and see the impact before saving.",
  "fix.classification":"Classification","fix.impactPreview":"Impact Preview",
  "fix.apply":"Apply Classification",
  // Trial
  "trial.remaining":"days remaining on trial","trial.expired":"Trial expired — read-only mode",
  "trial.upgrade":"Upgrade to regain full control and execution","trial.upgradeNow":"Upgrade Now",
  // Tax
  "tax.authority":"Tax Authority",
  // Priorities
  "priority.CRITICAL":"Critical","priority.HIGH":"High","priority.MEDIUM":"Medium","priority.LOW":"Low",
  // Statuses
  "status.OPEN":"Open","status.PAID":"Paid","status.CANCELLED":"Cancelled",
  // Ownership states
  "ownership.OWNED":"Owned","ownership.CUSTODY":"Custody","ownership.RESTRICTED":"Restricted",
  "ownership.ASSIGNED":"Assigned","ownership.ENCUMBERED":"Encumbered",
  // Permissions
  "permission.FREE":"Free","permission.RESTRICTED":"Restricted","permission.PROHIBITED":"Prohibited",
  // Pricing
  "pricing.title":"Stop spending money that isn't yours.",
  "pricing.subtitle":"Most tools track transactions. Sentient Vault helps protect your business from failing its financial obligations.",
};

// Sentient Vault is locked to English. The previous multi-language
// dictionaries (fr, de, es, pt, it, ja, zh, ar) have been removed —
// every UI string resolves through the English dictionary above.
const translations = { en };

export function t(key) {
  return translations.en[key] || key;
}

export function getLangDir() {
  // Always left-to-right — system is English-only.
  return "ltr";
}

export { translations };
export default t;
